import { useStore, selectCreature } from '@/infra/store';
import { mkCreature } from '@/domain/creature/factory';
import { randomGenome } from '@/domain/genetics/random';
import { uuid } from '@/infra/util/uuid';
import { clearSnapshot, saveCurrentSnapshot } from '@/services/save';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';

const ASSET_ROOT = '/assets/generated/house';

export function MemorialModal() {
  const t = useT();
  const c = useStore(selectCreature);
  if (!c || !c.state.dead) return null;

  const handleNewLife = async (): Promise<void> => {
    await clearSnapshot();
    const fresh = mkCreature({ genome: randomGenome(), id: uuid(), nowMs: Date.now() });
    useStore.setState({ creature: fresh });
    await saveCurrentSnapshot();
  };

  const cause = c.state.causeOfDeath ?? 'old-age';
  const causeLabel = t(`lineage.cause.${cause}`);

  return (
    <div role="alertdialog" data-testid="memorial-modal" style={overlayStyle}>
      <div style={{ ...cardStyle, maxWidth: 430, textAlign: 'center' }}>
        <img src={`${ASSET_ROOT}/icon-lineage.png`} alt="" draggable={false} style={heroIcon} />
        <h2 style={title}>{t('memorial.title', { name: c.name })}</h2>
        <p style={body}>{t('memorial.cause', { cause: causeLabel })}</p>
        <button
          data-testid="btn-new-life"
          onClick={() => void handleNewLife()}
          style={primaryBtn}
        >
          <img src={`${ASSET_ROOT}/icon-breeding.png`} alt="" draggable={false} style={buttonIcon} />
          {t('memorial.newLife')}
        </button>
      </div>
    </div>
  );
}

const title: React.CSSProperties = {
  margin: `0 0 ${SPACING.sm}px 0`,
  color: COLORS.accent,
  fontSize: FONT.sizeLg,
};
const body: React.CSSProperties = {
  margin: `0 0 ${SPACING.lg}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
};
const heroIcon: React.CSSProperties = {
  width: 58,
  height: 58,
  objectFit: 'contain',
  imageRendering: 'auto',
  margin: `0 auto ${SPACING.md}px auto`,
  display: 'block',
};
const primaryBtn: React.CSSProperties = {
  background: COLORS.ok,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 8,
  padding: '12px 30px',
  cursor: 'pointer',
  fontSize: FONT.sizeLg,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: SPACING.sm,
};
const buttonIcon: React.CSSProperties = {
  width: 42,
  height: 42,
  objectFit: 'contain',
  imageRendering: 'auto',
};
